import { forwardRef } from 'react';
import type { LucideIcon } from 'lucide-react';

import { Badge } from './Badge';

export interface FilterChipProps extends React.ButtonHTMLAttributes<HTMLButtonElement> {
  icon?: LucideIcon;
  children: React.ReactNode;
  count?: number;
  selected?: boolean;
  size?: 'sm' | 'md';
}

function cn(...parts: Array<string | false | null | undefined>): string {
  return parts.filter(Boolean).join(' ');
}

const chipSizeClasses = {
  sm: 'h-7 px-2 text-xs gap-1.5',
  md: 'h-8 px-3 text-sm gap-2',
} as const;

export const FilterChip = forwardRef<HTMLButtonElement, FilterChipProps>(function FilterChip(
  {
    icon: Icon,
    children,
    count,
    selected = false,
    size = 'md',
    type = 'button',
    className,
    ...rest
  },
  ref,
) {
  return (
    <button
      ref={ref}
      type={type}
      className={cn(
        'inline-flex items-center rounded-full border font-medium whitespace-nowrap',
        'transition-colors duration-150 motion-reduce:transition-none',
        'focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[color:var(--color-focus-ring)] focus-visible:ring-offset-2 focus-visible:ring-offset-[color:var(--color-focus-ring-offset)]',
        chipSizeClasses[size],
        selected
          ? 'border-[color:var(--color-primary)] bg-[color:var(--color-selected-bg)] text-[color:var(--color-primary)]'
          : 'border-[color:var(--color-border-subtle)] bg-transparent text-[color:var(--color-text-muted)] hover:border-[color:var(--color-border-strong)] hover:bg-[color:var(--color-surface-raised)]',
        className,
      )}
      aria-pressed={rest['aria-pressed'] ?? selected}
      {...rest}
    >
      {Icon ? <Icon className={cn(size === 'sm' ? 'h-3.5 w-3.5' : 'h-4 w-4')} aria-hidden="true" /> : null}
      <span>{children}</span>
      {typeof count === 'number' ? (
        <Badge
          variant="neutral"
          size="sm"
          className="hidden min-[480px]:inline-flex"
        >
          {count}
        </Badge>
      ) : null}
    </button>
  );
});
